<template>
	<div class="calculator">
		<header class="calculator__header" ref="header">
			<Corridor>
				<h2 class="calculator__title">Recyculator</h2>
				<p class="calculator__prompt">
					Tap each of the containers and use the sliders to estimate your weekly
					usage.
				</p>
			</Corridor>
		</header>
		<div class="calculator__tabs" ref="tabs">
			<component
				v-for="tab in tabs"
				v-model="tab.value"
				:key="tab.name"
				:name="tab.name"
				:is="tab.component"
				:height="tabHeight"
				:open="isOpen(tab.name)"
				@toggle="onToggle(tab.name)"
			/>
			<TabResults
				:name="resultsTab"
				:open="isOpen(resultsTab)"
				:height="tabHeight"
				:values="tabs"
				@toggle="onToggle(resultsTab)"
			/>
		</div>
	</div>
</template>

<script>
import Corridor from '@/layouts/Corridor'
import TabPlastic from '@/components/TabPlastic'
import TabAluminium from '@/components/TabAluminium'
import TabGlass from '@/components/TabGlass'
import TabResults from '@/components/TabResults'

const createTab = (name, component) => ({ name, component, value: 0 })
const tabUrl = tab => '/calculator' + (tab ? `/${tab}` : '')

// Scroll tween settings
const TWEEN_OPTS = {
	duration: 300,
	ease: t => 1 - Math.pow(t - 1, 2)
}

export default {
	name: 'Calculator',
	props: {
		tab: String
	},
	data() {
		return {
			contentHeight: 0,
			scrollTween: null,
			resultsTab: 'results',
			tabs: [
				createTab('plastic', TabPlastic),
				createTab('aluminium', TabAluminium),
				createTab('glass', TabGlass)
			]
		}
	},
	computed: {
		numTabs() {
			return this.tabs.length + 1
		},
		tabHeight() {
			return this.contentHeight / this.numTabs
		},
		openTab() {
			return this.$route.params.tab
		}
	},
	methods: {
		measure() {
			const headerHeight = this.$refs.header.offsetHeight
			const appOffset = this.$appHeightOffset
			this.contentHeight = this.$screen.height - headerHeight - appOffset
		},
		isOpen(name) {
			return name === this.openTab
		},
		stopScroll() {
			if (this.scrollTween) this.removeTween(this.scrollTween)
		},
		scrollTo(name) {
			if (name) {
				// Get scrollable container
				const elem = document.querySelector('main')

				// Calculate scroll target
				const top = this.$refs.header.offsetHeight
				const index = this.getIndex(name)
				const to = top + this.tabHeight * index

				// Scroll to opened tab
				this.scrollTween = this.addTween(
					{ from: elem.scrollTop, to, ...TWEEN_OPTS },
					y => (elem.scrollTop = y)
				)
			}
		},
		onToggle(name) {
			if (this.isOpen(name)) {
				// Close currently open tab
				this.$router.go(-1)
			} else {
				// Stop any existing scroll
				this.stopScroll()
				// Push or replace depending on if a tab is already open
				const target = tabUrl(name)
				if (this.openTab) this.$router.replace(target)
				else this.$router.push(target)
			}
		},
		getIndex(name) {
			if (name === this.resultsTab) return this.numTabs - 1
			else return this.tabs.findIndex(tab => tab.name === name)
		}
	},
	mounted() {
		// Measure straight away, then again after fonts load
		this.measure()
		document.fonts.ready.then(() => {
			this.$nextTick(() => {
				this.measure()
				this.scrollTo(this.$route.params.tab)
			})
		})
	},
	watch: {
		'$route.params.tab'(next) {
			this.scrollTo(next)
		},
		'$screen.height'() {
			this.measure()
		}
	},
	components: {
		Corridor,
		TabPlastic,
		TabAluminium,
		TabGlass,
		TabResults
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';

.calculator {
	height: 100%;

	&__header {
		background-color: $green-44;
		padding: 1.5rem 0;
		color: white;
	}
	&__title {
		margin: 0 0 0.5rem;
	}
	&__prompt {
		margin: 0;
		font-size: 0.875rem;
	}
}
</style>
